import { inject, Injectable } from '@angular/core';
import { defer, type Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { BleRestProxyService } from './ble-rest-proxy.service';
import { LOG_DEBUG } from '../config/debug.config';

import type { DeviceChallengeResponse } from '@hunting-nature/shared-core/interfaces/device-registration.interface';

/** REST-over-BLE endpoints for the device-signing handshake (mirrors the Unix-socket test). */
const DEVICE_SIGNING_CHALLENGE_ENDPOINT = '/api/v1/services_ulm/MaintenanceService/DeviceSigningChallenge/value';
const DEVICE_SIGNING_RESPONSE_ENDPOINT = '/api/v1/services_ulm/MaintenanceService/DeviceSigningResponse/value';

/** Challenge layout: 32-byte nonce followed by 512-byte backend signature. */
const NONCE_LENGTH = 32;
const BACKEND_SIGNATURE_LENGTH = 512;
const CHALLENGE_LENGTH = NONCE_LENGTH + BACKEND_SIGNATURE_LENGTH;
const DEVICE_SIGNATURE_LENGTH = 512;

/**
 * Service for handling BLE communication with Cannes devices during the registration flow.
 */
@Injectable({
  providedIn: 'root',
})
export class CannesBleRegistrationService {
  private readonly bleRestProxy = inject(BleRestProxyService);

  /**
   * Writes the DeviceSigningChallenge to the device via BLE REST proxy.
   *
   * The 544-byte challenge (nonce || backendSignature) is sent as a PUT whose body is a
   * JSON array of bytes (e.g. `[228,182,...]`), exactly like the firmware's own Unix-socket
   * test (`testDeviceSigningChallenge.sh`). The BLE REST proxy tunnels this as the HTTP
   * entity body; the firmware parses it into a `std::vector<uint8_t>`.
   *
   * The serialized body (~1940 bytes) exceeds the 512-byte BLE attribute limit, so the proxy
   * transparently splits it into `Range: "bytes start-end/total"` chunks per the firmware's
   * Content-Range protocol (HttpProxyService). No chunking is needed at this layer.
   */
  public writeDeviceSigningChallenge$(deviceId: string, challenge: DeviceChallengeResponse): Observable<void> {
    return defer(() => {
      const challengeBytes = this.encodeChallengeToBytes(challenge);
      const challengeArray = Array.from(challengeBytes);

      if (LOG_DEBUG) {
        console.log('[CannesBleRegistration] Writing DeviceSigningChallenge:', {
          deviceId,
          challengeId: challenge.challengeId,
          totalBytes: challengeArray.length,
        });
      }

      return this.bleRestProxy.put$<void>(deviceId, DEVICE_SIGNING_CHALLENGE_ENDPOINT, challengeArray).pipe(
        tap(() => {
          if (LOG_DEBUG) console.log('[CannesBleRegistration] DeviceSigningChallenge write accepted by device');
        }),
        map(() => void 0),
        catchError((error: unknown) => {
          console.error('[CannesBleRegistration] Failed to write DeviceSigningChallenge', error);
          throw new Error('BLE_WRITE_CHALLENGE_FAILED');
        })
      );
    });
  }

  /**
   * Reads the DeviceSigningResponse (512-byte device counter-signature) from the device.
   *
   * The proxy returns the body as a JSON array of bytes, which is normalized here to a
   * `Uint8Array`. An all-zero response means the device rejected the challenge.
   */
  public readDeviceSigningResponse$(deviceId: string): Observable<Uint8Array> {
    if (LOG_DEBUG) console.log('[CannesBleRegistration] Reading DeviceSigningResponse from device');

    return this.bleRestProxy.get$<Array<number> | Uint8Array>(deviceId, DEVICE_SIGNING_RESPONSE_ENDPOINT).pipe(
      map(response => {
        const signature = response instanceof Uint8Array ? response : Uint8Array.from(response ?? []);
        const allZero = signature.length > 0 && signature.every(byte => byte === 0);

        if (LOG_DEBUG) {
          console.log('[CannesBleRegistration] DeviceSigningResponse read:', {
            length: signature.length,
            allZero,
          });
        }

        if (signature.length === 0) {
          throw new Error('BLE_EMPTY_SIGNATURE');
        }
        if (signature.length !== DEVICE_SIGNATURE_LENGTH) {
          throw new Error('BLE_SIGNATURE_LENGTH_MISMATCH');
        }
        if (allZero) {
          // Device verified the challenge and rejected it (bad backend signature / wrong size / stale).
          throw new Error('BLE_DEVICE_REJECTED_CHALLENGE');
        }

        return signature;
      }),
      catchError((error: unknown) => {
        console.error('[CannesBleRegistration] Failed to read DeviceSigningResponse', error);
        throw error instanceof Error ? error : new Error('BLE_READ_SIGNATURE_FAILED');
      })
    );
  }

  /**
   * Decodes the Base64 nonce and backend signature and concatenates them into the
   * raw 544-byte challenge (nonce || backendSignature), validating the component lengths.
   */
  private encodeChallengeToBytes(challenge: DeviceChallengeResponse): Uint8Array {
    const nonce = this.base64ToBytes(challenge.nonce);
    const backendSignature = this.base64ToBytes(challenge.backendSignature);

    if (nonce.length !== NONCE_LENGTH) {
      throw new Error(`Invalid nonce length: ${nonce.length}, expected ${NONCE_LENGTH}`);
    }
    if (backendSignature.length !== BACKEND_SIGNATURE_LENGTH) {
      throw new Error(`Invalid backend signature length: ${backendSignature.length}, expected ${BACKEND_SIGNATURE_LENGTH}`);
    }

    const challengeBytes = new Uint8Array(CHALLENGE_LENGTH);
    challengeBytes.set(nonce, 0);
    challengeBytes.set(backendSignature, NONCE_LENGTH);
    return challengeBytes;
  }

  /** Decodes a Base64 string into bytes using the browser-native `atob` (no Node Buffer polyfill needed). */
  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  }
}
